import { BrowseImageCard } from '@/components/discovery/browse-image-card'
import type { BrowseProject } from '@/api/types'
import { TILE_SIZE_WIDTH, type TileSize } from '@/features/discovery/use-browse-tile-sizes'

interface BrowseInspirationGridProps {
  projects: BrowseProject[]
  isAuthenticated: boolean
  isFavoritedByMe: (projectId: string) => boolean
  /** User-controlled column count from the sticky-header slider. */
  columns: number
  /** User-selected subset of tile sizes to draw from. */
  enabledSizes: Set<TileSize>
  expandedIds: Set<string>
  onToggleExpanded: (projectId: string) => void
}

/**
 * Bento-style CSS Grid for "Inspiration" mode. Tile sizes come from a fixed repeating pattern
 * inspired by a stepping-stone paver mold; the pattern is filtered at render to whatever the
 * user has enabled in the sizes checklist, so they can A/B different layouts (all-small,
 * verticals-only, big-anchors, etc.). {@code grid-auto-flow: dense} backfills gaps so the
 * layout stays tight when the pattern cycles mid-row or filtering shortens it.
 *
 * <p>Column count is user-controlled via the header slider. The full pattern matches the mold
 * reference most precisely at 3 cols; other column counts rearrange via dense auto-flow but
 * keep the same density mix.
 *
 * <p>The engagement-score helper in {@code features/discovery/mosaic-score.ts} still exists
 * for the deferred "popular projects get the big cells" v2 — for now the pattern is purely
 * positional so the layout reads as a consistent mood board.
 */
export const BrowseInspirationGrid = ({
  projects,
  isAuthenticated,
  isFavoritedByMe,
  columns,
  enabledSizes,
  expandedIds,
  onToggleExpanded,
}: BrowseInspirationGridProps) => {
  // Two-stage filter: (a) user's checkbox selection, (b) anything wider than the user's
  // density target gets dropped so the grid never tries to lay out a tile that exceeds the
  // intended max-density.
  const activePattern = FULL_PATTERN.filter(
    (size) => enabledSizes.has(size) && TILE_SIZE_WIDTH[size] <= columns
  )
  // The hook guarantees enabledSizes.size >= 1, but the pattern might not contain every
  // selected size, so guard against an empty filtered pattern just in case.
  const safePattern = activePattern.length > 0 ? activePattern : (['small'] as const)
  // Map the slider's "density target" to a CSS minmax floor. The grid uses `auto-fill` so
  // narrower viewports collapse to fewer columns even when the user has dragged toward
  // "more"; on a typical desktop (~1200px content width) the slider value ≈ the rendered
  // column count. Clamped so extreme slider settings still produce sensible cell widths.
  const minColPx = Math.max(120, Math.min(500, Math.round(1200 / columns)))
  return (
    <div
      className="grid gap-3"
      style={{
        gridTemplateColumns: `repeat(auto-fill, minmax(${minColPx}px, 1fr))`,
        gridAutoFlow: 'dense',
        gridAutoRows: '180px',
      }}
    >
      {projects.map((project, index) => {
        const isExpanded = expandedIds.has(project.id)
        // Expanded card grows to a 2×3 cell (or as wide as the grid allows) — gives the
        // info overlay room without dominating the layout. Dense flow reflows around it.
        // Static class strings here so Tailwind's source scanner picks them up.
        const patternCell = cellClass(safePattern[index % safePattern.length])
        const finalCell = isExpanded
          ? columns >= 2
            ? 'col-span-2 row-span-3'
            : 'col-span-1 row-span-3'
          : patternCell
        return (
          <div key={project.id} className={finalCell}>
            <BrowseImageCard
              project={project}
              isAuthenticated={isAuthenticated}
              isFavoritedByMe={isFavoritedByMe(project.id)}
              isExpanded={isExpanded}
              onToggleExpanded={onToggleExpanded}
            />
          </div>
        )
      })}
    </div>
  )
}

/**
 * The full repeating tile sequence — every available size makes at least one appearance, so
 * a user toggling on a previously-disabled size sees it immediately. Render-time filtering
 * trims this list to whatever's enabled AND fits the current column count.
 *
 * <p>Order matters for dense auto-flow: bigger anchors land first, smalls fill around them.
 * Iterate freely — swap entries, lengthen the period, etc.
 */
const FULL_PATTERN: readonly TileSize[] = [
  'tall', // 1×2  vertical anchor
  'featured', // 2×2
  'tall', // 1×2
  'small', // 1×1
  'wideXL', // 3×1  extra wide horizontal
  'tall', // 1×2
  'small', // 1×1
  'wide', // 2×1
  'featuredTall', // 2×3  vertical big-anchor
  'small', // 1×1
  'tallXL', // 1×3  extra tall vertical
  'small', // 1×1
  'featuredWide', // 3×2  wide featured
  'small', // 1×1
  'tall', // 1×2
  'small', // 1×1
  'featuredXL', // 3×3  huge anchor
  'small', // 1×1
  'tall', // 1×2
  'small', // 1×1
]

const cellClass = (size: TileSize): string => {
  switch (size) {
    case 'featuredXL':
      return 'col-span-3 row-span-3'
    case 'featuredWide':
      return 'col-span-3 row-span-2'
    case 'tallXL':
      return 'col-span-1 row-span-3'
    case 'wideXL':
      return 'col-span-3 row-span-1'
    case 'featuredTall':
      return 'col-span-2 row-span-3'
    case 'featured':
      return 'col-span-2 row-span-2'
    case 'wide':
      return 'col-span-2 row-span-1'
    case 'tall':
      return 'col-span-1 row-span-2'
    case 'small':
    default:
      return 'col-span-1 row-span-1'
  }
}
